'use client'
import { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import { useScroll, useTransform, motion, type Variants } from 'framer-motion'

const NAV_LINKS = [
  { href: '#services', label: 'Services' },
  { href: '#broker-gateway', label: 'Brokers' },
  { href: '#shippers', label: 'Shippers' },
  { href: '#careers', label: 'Apply' },
]

const linkVariants: Variants = {
  hidden: { opacity: 0, y: -8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
}

export function NavBar() {
  const { scrollY } = useScroll()
  const bgOpacity = useTransform(scrollY, [0, 80], [0.3, 0.88])
  const [isOpen, setIsOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!isOpen) return
    function handleOutside(e: MouseEvent) {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [isOpen])

  return (
    <motion.header
      ref={headerRef as React.RefObject<HTMLElement>}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={`sticky top-0 w-full border-b border-white/10 backdrop-blur-md${isOpen ? '' : ' overflow-hidden'}`}
      style={{ zIndex: 'var(--z-sticky)' } as React.CSSProperties}
    >
      {/* Scroll-aware background overlay */}
      <motion.div
        className="absolute inset-0 bg-[color:var(--bg-base)] pointer-events-none"
        style={{ opacity: bgOpacity }}
        aria-hidden="true"
      />

      <div className="relative page-container flex items-center justify-between h-16">
        <Link
          href="/"
          className="text-xl font-bold text-white tracking-tight"
          aria-label="ADG2 Transport Corp — home"
        >
          ADG2{' '}
          <span className="text-[color:var(--accent)]">Transport Corp</span>
        </Link>

        <button
          className="md:hidden text-white p-2 -mr-2"
          aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isOpen}
          aria-controls="mobile-nav"
          onClick={() => setIsOpen(prev => !prev)}
        >
          <span className="text-2xl leading-none select-none" aria-hidden="true">☰</span>
        </button>

        <nav aria-label="Main navigation">
          <motion.ul
            className="hidden md:flex items-center gap-8"
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.25 } } }}
          >
            {NAV_LINKS.map(({ href, label }) => (
              <motion.li key={href} variants={linkVariants}>
                <a
                  href={href}
                  className="text-sm font-medium text-[color:var(--foreground-muted)] hover:text-white transition-colors"
                >
                  {label}
                </a>
              </motion.li>
            ))}
          </motion.ul>
        </nav>
      </div>

      {isOpen && (
        <nav
          id="mobile-nav"
          aria-label="Mobile navigation"
          className="md:hidden relative border-t border-white/10 bg-[color:var(--bg-base)]"
        >
          <ul className="flex flex-col py-2">
            {NAV_LINKS.map(({ href, label }) => (
              <li key={href}>
                <a
                  href={href}
                  className="block px-6 py-3 text-sm font-medium text-[color:var(--foreground-muted)] hover:text-white transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </motion.header>
  )
}
